// ---------------------------------------------------------------------------
// Inlined from ./app.ts
// ---------------------------------------------------------------------------

export const APP_NAME = 'Visualry';
export const APP_TAGLINE_SEGMENTS = [
  'Discover the',
  'prompts behind',
  'every image.',
] as const;
export const APP_TAGLINE = APP_TAGLINE_SEGMENTS.join(' ');
export const APP_DESCRIPTION =
  'Browse curated AI-generated images, reveal the prompts for free, and generate similar visuals when you need them.';

export const PROMPT_EVENT_TYPE = {
  VIEW: 'view',
  COPY: 'copy',
  SAVE: 'save',
  UNSAVE: 'unsave',
  GENERATE_CLICK: 'generate_click',
  SHARE: 'share',
  LOCK_SEEN: 'prompt_lock_seen',
  SIGNUP_CLICKED: 'signup_clicked',
  SIGNUP_COMPLETED: 'signup_completed',
  PROMPT_REVEALED: 'prompt_revealed',
} as const;

export const FUNNEL_EVENT_TYPES = [
  PROMPT_EVENT_TYPE.LOCK_SEEN,
  PROMPT_EVENT_TYPE.SIGNUP_CLICKED,
  PROMPT_EVENT_TYPE.SIGNUP_COMPLETED,
  PROMPT_EVENT_TYPE.PROMPT_REVEALED,
] as const;

export const GENERATION_JOB_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const;

export const CREDIT_LEDGER_REASON = {
  PURCHASE: 'purchase',
  SUBSCRIPTION_GRANT: 'subscription_grant',
  GENERATION_SPEND: 'generation_spend',
  GENERATION_REFUND: 'generation_refund',
  ADMIN_ADJUSTMENT: 'admin_adjustment',
  ADMIN_CREDIT_GRANT: 'admin_credit_grant',
} as const;

/** OpenAI GPT image models: low | medium | high | auto */
export const IMAGE_GENERATION_QUALITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  AUTO: 'auto',
} as const;

export type ImageGenerationQuality =
  (typeof IMAGE_GENERATION_QUALITY)[keyof typeof IMAGE_GENERATION_QUALITY];

export const IMAGE_GENERATION_QUALITY_VALUES = [
  IMAGE_GENERATION_QUALITY.LOW,
  IMAGE_GENERATION_QUALITY.MEDIUM,
  IMAGE_GENERATION_QUALITY.HIGH,
  IMAGE_GENERATION_QUALITY.AUTO,
] as const;

export const GENERATION_COST_BY_QUALITY: Record<ImageGenerationQuality, number> = {
  low: 4,
  medium: 4,
  high: 8,
  auto: 4,
};

export const REFERENCE_IMAGE_CREDIT_MULTIPLIER = 2;

export const SUBSCRIPTION_CREDIT_ALLOWANCE = {
  plus: 350,
  pro: 1000,
} as const;

export const CREDIT_USAGE_EXAMPLES = {
  standardText: 4,
  standardReference: 8,
  highText: 8,
  highReference: 16,
  ultraText: 15,
  ultraReference: 30,
} as const;

export const resolveGenerationCreditCost = (input: {
  quality: ImageGenerationQuality;
  hasReferenceImages?: boolean;
}): number => {
  const baseCost = GENERATION_COST_BY_QUALITY[input.quality];
  return input.hasReferenceImages ? baseCost * REFERENCE_IMAGE_CREDIT_MULTIPLIER : baseCost;
};

// ---------------------------------------------------------------------------
// Inlined from ./prompts.ts
// ---------------------------------------------------------------------------

export const LAUNCH_CATEGORIES: ReadonlyArray<{
  slug: string;
  name: string;
  description: string;
}> = [
  {
    slug: 'headshots',
    name: 'Headshots',
    description: 'AI portrait prompts for LinkedIn, actors, and creators.',
  },
  {
    slug: 'fashion',
    name: 'Fashion',
    description: 'Editorial fashion, campaigns, and lookbook visuals.',
  },
  {
    slug: 'kids',
    name: 'Kids',
    description: 'Playful, wholesome AI imagery for children and family brands.',
  },
  {
    slug: 'brand-ads',
    name: 'Brand Ads',
    description: 'Hero shots and advertising visuals for premium brands.',
  },
  {
    slug: 'film-posters',
    name: 'Film Posters',
    description: 'Cinematic poster art and movie-style compositions.',
  },
  {
    slug: 'product-photography',
    name: 'Product Photography',
    description: 'Studio, lifestyle, and hero product photography prompts.',
  },
  {
    slug: 'ecommerce',
    name: 'Ecommerce',
    description: 'Clean, conversion-ready ecommerce imagery.',
  },
  { slug: 'food', name: 'Food', description: 'Editorial food photography and recipe visuals.' },
  {
    slug: 'real-estate',
    name: 'Real Estate',
    description: 'Architecture, interiors, and listing hero imagery.',
  },
  {
    slug: 'interior-design',
    name: 'Interior Design',
    description: 'Modern, mid-century, and luxury interior moods.',
  },
  {
    slug: 'luxury',
    name: 'Luxury',
    description: 'High-end luxury campaigns and prestige brand visuals.',
  },
  {
    slug: 'social-media',
    name: 'Social Media',
    description: 'Scroll-stopping creators content for social platforms.',
  },
  {
    slug: 'beauty',
    name: 'Beauty',
    description: 'Skincare, cosmetics, and beauty editorial visuals.',
  },
  {
    slug: 'fitness',
    name: 'Fitness',
    description: 'Athletic, training, and wellness photography.',
  },
  {
    slug: 'travel',
    name: 'Travel',
    description: 'Travel, destinations, and lifestyle photography.',
  },
  {
    slug: 'automotive',
    name: 'Automotive',
    description: 'Cars, motorcycles, and automotive hero shots.',
  },
  {
    slug: 'wedding',
    name: 'Wedding',
    description: 'Romantic, editorial wedding photography prompts.',
  },
  {
    slug: 'architecture',
    name: 'Architecture',
    description: 'Architectural renders and spatial compositions.',
  },
  {
    slug: 'anime',
    name: 'Anime',
    description: 'Stylised anime and illustration-inspired imagery.',
  },
  {
    slug: 'editorial',
    name: 'Editorial',
    description: 'Magazine-style editorial photography prompts.',
  },
] as const;

export const LAUNCH_MODELS: ReadonlyArray<{
  slug: string;
  name: string;
  provider: string;
  description: string;
}> = [
  {
    slug: 'gpt-image-2',
    name: 'GPT Image 2',
    provider: 'OpenAI',
    description: 'Latest GPT Image model. Strong instruction-following, photoreal, and text rendering.',
  },
  {
    slug: 'flux',
    name: 'Flux',
    provider: 'Black Forest Labs',
    description: 'Photorealistic, high-detail generation.',
  },
  {
    slug: 'midjourney',
    name: 'Midjourney',
    provider: 'Midjourney',
    description: 'Aesthetic and stylised imagery.',
  },
  {
    slug: 'gpt-image',
    name: 'GPT Image',
    provider: 'OpenAI',
    description: 'Reliable, instruction-following generation.',
  },
  {
    slug: 'ideogram',
    name: 'Ideogram',
    provider: 'Ideogram',
    description: 'Strong typography and lettering.',
  },
  {
    slug: 'imagen',
    name: 'Imagen',
    provider: 'Google',
    description: 'Photoreal and product imagery.',
  },
  {
    slug: 'recraft',
    name: 'Recraft',
    provider: 'Recraft',
    description: 'Vector and brand-style generation.',
  },
] as const;

// ---------------------------------------------------------------------------
// Inlined from ./polish-subject.ts
// Injected into the prompt when the user toggles "Polish subject" in
// the generate form. The text tells the AI model to use the uploaded
// reference image ONLY as an identity reference for the subject — keep
// the face consistent, but allow the new image to use a different
// pose, expression, clothing, background, and lighting.
//
// Kept in @visualry/shared so the API can inject it and the web app
// can show an accurate preview/description without duplicating the
// text on each side.
// ---------------------------------------------------------------------------

export const POLISH_SUBJECT_INSTRUCTIONS = `Subject:
Use the uploaded reference image only as an identity reference for subject.

Preserve:
- facial identity
- facial proportions
- eye color
- skin tone
- hairline
- recognizable facial features

Do NOT preserve:
- head angle
- pose
- facial expression
- clothing
- hairstyle
- background
- lighting
- camera angle`;

export const POLISH_SUBJECT_LABEL = 'Polish subject';
export const POLISH_SUBJECT_DESCRIPTION =
  'Use the reference image as an identity anchor — keep the face, change the scene, pose, and lighting.';

// ---------------------------------------------------------------------------
// Inlined from ./watermark.ts
// Default watermark text shown in the textbox when the user enables
// the watermark but does not type a custom value. Lowercase, no
// spaces — matches the style spec the AI is asked to render.
// ---------------------------------------------------------------------------

export const WATERMARK_DEFAULT_TEXT = 'visualry.ai';
export const WATERMARK_TEXT_MAX_LENGTH = 32;

/**
 * Sanitize a user-supplied watermark string before it is interpolated
 * into the AI prompt. Strips control characters, newlines, and
 * double-quotes so a malicious or accidental input cannot break out
 * of the quoted literal in the prompt or inject extra instructions.
 *
 * Falls back to the default text when the result is empty.
 */
export const sanitizeWatermarkText = (raw: string | undefined | null): string => {
  if (!raw) return WATERMARK_DEFAULT_TEXT;
  // Drop ASCII control chars (0x00-0x1F + 0x7F), tabs, newlines, and
  // the prompt-breaking characters: double-quote, backslash, semicolon,
  // and angle brackets.
  const cleaned = raw
    // eslint-disable-next-line no-control-regex -- intentional: sanitizing untrusted input
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/["\\;<>]/g, '')
    .trim();
  return cleaned.length > 0 ? cleaned.slice(0, WATERMARK_TEXT_MAX_LENGTH) : WATERMARK_DEFAULT_TEXT;
};

// Build the watermark instruction block for a given (user-supplied or
// default) text. Keeps the style spec in one place and lets the API
// and the web app both render accurate copy without duplicating it.
//
// Kept in @visualry/shared so the API can inject it and the web app
// can show an accurate label/description without duplicating the
// text on each side.
export const watermarkInstructions = (text: string = WATERMARK_DEFAULT_TEXT): string =>
  `Add a discreet photographer watermark reading exactly "${text}".

Style:
• lowercase
• minimalist
• luxury
• modern sans-serif
• clean kerning
• white or light gray
• semi-transparent
• bottom-right corner`;

// Back-compat export. Defaults to the visualry.ai literal so existing
// imports and tests keep working. Prefer watermarkInstructions() in
// new code.
export const WATERMARK_INSTRUCTIONS = watermarkInstructions();

export const WATERMARK_LABEL = 'Add watermark';
export const WATERMARK_DESCRIPTION =
  'Adds a discreet watermark in the bottom-right corner of the generated image.';
